import {
  PAYWALL_CLICKED_EVENT,
  PAYWALL_VISIBLE_EVENT,
  ZEPHR_CUSTOM_DATA_REQUEST,
  ZEPHR_DECISIONS_FINISHED_EVENT,
  ZEPHR_DISABLED_EVENT,
  ZEPHR_ERROR_EVENT,
  ZEPHR_REGISTER_CUSTOM_DATA_EVENT,
  ZEPHR_TIMEOUT_EVENT,
} from "./events.js";
import {
  appendTrackingParamsToLinks,
  findCheckoutLink,
  observePaywallVisibility,
} from "./paywall_utils";
import { rewriteUrl } from "./zephr_utils";

export type ZephrCustomData = {
  content_type?: string;
  content_main_section?: string;
  content_paid_category?: string;
  content_paid_status: "free" | "login";
  gallery_current_picture?: number;
  gallery_number_of_pictures?: number;
};

const template = document.createElement("template");

template.innerHTML = `
  <style>
    :host { display: block; position: relative; }

    :host .loading-container {
      align-items: center;
      background-color: var(--color-bg-subtle);
      display: flex;
      inset: 0;
      justify-content: center;
      position: absolute;
      z-index: 2;
    }

    :host([disabled]) .loading-container {
      display: none !important;
    }

    .paid-barrier--loading {
      opacity: 0;
    }

    .spinner {
      animation: spin 1s linear infinite;
      block-size: 40px;
      border-radius: 50%;
      border: 4px solid var(--color-border-default);
      border-block-start-color: var(--color-border-highlight);
      inline-size: 40px;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  </style>
  <div class="loading-container">
    <div class="spinner"></div>
  </div>
  <slot></slot>
`;

export class Paywall extends HTMLElement {
  #handled = false;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.shadowRoot!.appendChild(template.content.cloneNode(true));
    this.onFinish = this.onFinish.bind(this);
    this.onTimeout = this.onTimeout.bind(this);
    this.onDisabled = this.onDisabled.bind(this);
    this.bindPaywallVisibility = this.bindPaywallVisibility.bind(this);
  }

  get headline(): string {
    return this.getAttribute("headline") ?? "";
  }

  get envBaseUrl(): string {
    return this.getAttribute("envBaseUrl") ?? location.origin;
  }

  get contentId(): string {
    return this.getAttribute("contentid") ?? "";
  }

  get contentUrl(): string | undefined {
    const contentUrl = this.getAttribute("contenturl") ?? "";
    return contentUrl ? rewriteUrl(contentUrl, this.envBaseUrl) : "";
  }

  get isLoggedIn(): boolean {
    return this.getAttribute("loggedin") === "true";
  }

  get isDialog(): boolean {
    return this.getAttribute("dialog") === "true";
  }

  get isDebugMode(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("zephr") === "false" || urlParams.get("zephr") === "0";
  }

  get type(): string {
    return this.getAttribute("type") ?? "";
  }

  get subtype(): string {
    return this.getAttribute("subtype") ?? "";
  }

  get trackingType(): string {
    return this.getAttribute("trackingtype") ?? "";
  }

  get zone(): string {
    return this.getAttribute("zone") ?? "";
  }

  get paidCategory(): string {
    return this.getAttribute("paidcategory") ?? "";
  }

  connectedCallback() {
    const zephrElement = document.getElementsByTagName("ws-zephr");

    if (zephrElement.length !== 0) {
      this.#sendCustomData();

      document.addEventListener(ZEPHR_TIMEOUT_EVENT, this.onTimeout);
      document.addEventListener(ZEPHR_DECISIONS_FINISHED_EVENT, this.onFinish);
      document.addEventListener(
        ZEPHR_DECISIONS_FINISHED_EVENT,
        this.bindPaywallVisibility,
      );
      document.addEventListener(ZEPHR_DISABLED_EVENT, this.onDisabled);
    } else {
      const paywallCta = this.querySelector("[data-gtm-paywall-cta]");
      paywallCta?.addEventListener("click", () => {
        document.dispatchEvent(new Event(PAYWALL_CLICKED_EVENT));
      });
    }
    document.addEventListener(ZEPHR_CUSTOM_DATA_REQUEST, this.#sendCustomData);
  }

  disconnectedCallback() {
    document.removeEventListener(ZEPHR_TIMEOUT_EVENT, this.onTimeout);
    document.removeEventListener(ZEPHR_DISABLED_EVENT, this.onDisabled);
    document.removeEventListener(ZEPHR_DECISIONS_FINISHED_EVENT, this.onFinish);
    document.removeEventListener(
      ZEPHR_DECISIONS_FINISHED_EVENT,
      this.bindPaywallVisibility,
    );
    document.removeEventListener(
      ZEPHR_CUSTOM_DATA_REQUEST,
      this.#sendCustomData,
    );

    const paywallCta = this.querySelector("[data-gtm-paywall-cta]");
    paywallCta?.removeEventListener("click", () => {
      document.dispatchEvent(new Event(PAYWALL_CLICKED_EVENT));
    });
  }

  onTimeout() {
    this.#removeLoadingUI();
  }
  onDisabled() {
    this.#removeLoadingUI();
  }

  onFinish() {
    if (this.#handled) return;
    this.#handled = true;
    const barrier = this.accessZephrBarrier();

    if (barrier) {
      this.#handleDecision(barrier);
    } else {
      const errorMessage = "No paid barrier found for Zephr decision tracking.";
      const error = new Error(errorMessage);
      document.dispatchEvent(
        new CustomEvent(ZEPHR_ERROR_EVENT, { detail: { error } }),
      );
      console.warn("Zephr error:", error.message);
    }
    this.#removeLoadingUI();
  }

  #handleDecision(barrier: Element) {
    const replaced = this.#cleanUpBarrier(barrier);
    this.#bindCheckoutClick(replaced);
    this.#appendTrackingParams(replaced);
  }

  #cleanUpBarrier(barrier: Element): HTMLElement {
    barrier.classList.remove("paid-barrier--loading");
    return (this.querySelector(".paid-barrier--zephr") ??
      barrier) as HTMLElement;
  }

  #bindCheckoutClick(replaced: HTMLElement) {
    const checkout = findCheckoutLink(replaced);
    if (checkout) {
      checkout.addEventListener("click", () => {
        document.dispatchEvent(new Event(PAYWALL_CLICKED_EVENT));
      });
    }
  }

  #appendTrackingParams(replaced: HTMLElement) {
    const params = this.#buildTrackingParams();
    appendTrackingParamsToLinks(replaced, params);
  }

  #removeLoadingUI() {
    const loadingContainer =
      this.shadowRoot?.querySelector<HTMLElement>(".loading-container");
    loadingContainer?.remove();
    this.accessPaidBarrier()?.classList.remove("paid-barrier--loading");
  }

  #sendCustomData = () => {
    document.dispatchEvent(
      new CustomEvent(ZEPHR_REGISTER_CUSTOM_DATA_EVENT, {
        detail: this.getCustomData(),
      }),
    );
  };

  #buildTrackingParams(): string {
    const contentId = this.contentId;
    const contentUrl = this.contentUrl;
    const params = [
      contentId ? `contentId=${encodeURIComponent(contentId)}` : "",
      contentUrl ? `contentUrl=${encodeURIComponent(contentUrl)}` : "",
      `source=paywall`,
    ];

    if (this.contentUrl) {
      try {
        const url = new URL(this.contentUrl);
        url.searchParams.set("cc_bust", Date.now().toString());
        params.push(`wdycf=${encodeURIComponent(url.toString())}`);
      } catch (error) {
        console.warn((error as Error).message);
      }
    }

    return params.filter(Boolean).join("&");
  }

  protected bindPaywallVisibility() {
    const barrier = this.accessZephrBarrier();

    if (!barrier) {
      console.warn("Zephr error: No paid barrier found");
      return;
    }

    observePaywallVisibility(barrier, () => {
      this.dispatchEvent(
        new CustomEvent(PAYWALL_VISIBLE_EVENT, {
          bubbles: true,
          composed: true,
        }),
      );
    });
  }

  protected getCustomData(
    galleryCurrentItem?: number,
    galleryTotalItems?: number,
  ): ZephrCustomData {
    return {
      content_type: this.trackingType,
      content_main_section: this.zone,
      content_paid_category: this.paidCategory,
      content_paid_status: this.paidCategory ? "login" : "free",
      ...(galleryCurrentItem !== undefined && {
        gallery_current_picture: galleryCurrentItem,
      }),
      ...(galleryTotalItems !== undefined && {
        gallery_number_of_pictures: galleryTotalItems,
      }),
    };
  }

  getOfferData(): string {
    const barrier = this.accessPaidBarrier();
    const offerData = barrier?.dataset?.offer;
    if (!offerData) {
      return "not_set";
    }

    return JSON.parse(offerData);
  }

  accessPaidBarrier(): HTMLElement | null {
    return (
      this.shadowRoot?.querySelector(".paid-barrier") ||
      this.querySelector(".paid-barrier")
    );
  }

  accessZephrBarrier(): HTMLElement | null {
    return (
      this.shadowRoot?.querySelector(".paid-barrier--zephr") ||
      this.querySelector(".paid-barrier--zephr")
    );
  }
}

if (!customElements.get("ws-paywall")) {
  customElements.define("ws-paywall", Paywall);
}

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface HTMLElementTagNameMap {
    "ws-paywall": Paywall;
  }

  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    Zephr: {
      includeOutcomes?: boolean;
      outcomesAsEvents?: boolean;
      groupFields?: boolean;
      outcomes?: Record<
        string,
        {
          featureLabel: string;
          outcomeId: string;
          outcomeLabel: string;
        }
      >;
      accessDetails?: {
        trials?: Record<
          string,
          {
            totalCredits: number;
            remainingCredits: number;
            reportInDataLayer?: boolean;
            dataLayerCreditsUsedKey?: string;
            dataLayerCreditsRemainingKey?: string;
          }
        >;
        trialTrackingDetails?: {
          entitlementType: string;
          entitlementId: string;
          creditsRemainingKey?: string;
          creditsUsedKey?: string;
        }[];
        credits?: Record<
          string,
          { totalCredits: number; remainingCredits: number }
        >;
        meters?: Record<
          string,
          { totalCredits: number; remainingCredits: number }
        >;
        timeTrials?: Record<
          string,
          {
            reportInDataLayer?: boolean;
            dataLayerTrialDurationKey?: string;
            dataLayerTrialDurationUnitsKey?: string;
            dataLayerTrackerEntryTimeKey?: string;
            dataLayerTrackerHitsKey?: string;
            dataLayerTrialTimeRemainingKey?: string;
            trialDuration?: number;
            trialDurationUnits?: string;
            entryTime?: number;
            trackerHits?: number;
            timeRemainingInTrial?: number;
          }
        >;
      };
    };
    [key: string]: any;
  }
}
